import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { ResponsibleForm as ResponsibleFormType, getResponsibleFormSchema } from "../../../types/forms/entity";
import { EntityResponsible } from "../../../types/model/entity/EntityResponsible";

interface ResponsibleFormProps {
  responsibleData?: EntityResponsible;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: ResponsibleFormType) => Promise<void>;
}

export default function ResponsibleForm({
  responsibleData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<ResponsibleFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getResponsibleFormSchema(t);
  const methods = useForm<ResponsibleFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: responsibleData?.name || "",
      section: responsibleData?.section || "",
      email: responsibleData?.email || "",
      mobile: responsibleData?.mobile || "",
      phone: responsibleData?.phone || "",
    },
  });

  const handleFormSubmit = async (values: ResponsibleFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.responsibles.name")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("name")}
              error={!!methods.formState.errors.name}
              helperText={methods.formState.errors.name?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.responsibles.section")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("section")}
              error={!!methods.formState.errors.section}
              helperText={methods.formState.errors.section?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <TextField
              fullWidth
              label={t("entities.entity.responsibles.email")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("email")}
              error={!!methods.formState.errors.email}
              helperText={methods.formState.errors.email?.message as string | undefined}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t("entities.entity.responsibles.mobile")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("mobile")}
              error={!!methods.formState.errors.mobile}
              helperText={methods.formState.errors.mobile?.message as string | undefined}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t("entities.entity.responsibles.phone")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("phone")}
              error={!!methods.formState.errors.phone}
              helperText={methods.formState.errors.phone?.message as string | undefined}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}